import { useRef, useState } from 'react';
import LiveCard from './LiveCard';
import { FBLive } from '@/types/fb-type';
import { Calendar } from 'lucide-react';

interface LiveVideoListProps {
  lives: FBLive[];
  isLoading?: boolean;
  onSelect: (fbLive: FBLive | null) => void;
}

export function LiveVideoList({ lives, onSelect, isLoading }: LiveVideoListProps) {
  const [selectedLiveId, setSelectedLiveId] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const handleSelect = (live: FBLive) => {
    if (selectedLiveId === live.id) {
      setSelectedLiveId(null);
      onSelect(null);
    } else {
      setSelectedLiveId(live.id);
      onSelect(live);
    }
  };

  if (isLoading && lives.length === 0) {
    return (
      <div className="flex flex-col gap-3 max-h-[520px]">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 border border-gray-200 rounded-lg animate-pulse">
            <div className="flex gap-3">
              <div className="w-16 h-16 bg-gray-200 rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (lives.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <div className="mb-4 flex justify-center">
          <Calendar size={48} />
        </div>
        <h3 className="text-lg font-medium mb-2">No active live sessions</h3>
        <p className="text-sm">Start a new live session to connect with customers</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 max-h-[520px]">
      {isLoading && lives.length > 0 && (
        <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      )}
      {lives.map((live, index) => (
        <LiveCard
          key={live.id}
          live={live}
          isSelected={live.id === selectedLiveId}
          onSelect={() => handleSelect(live)}
          priority={index === 0}
        />
      ))}
    </div>
  );
}
